import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { addToOrderPrebookItemDisabled } from '@/contexts/item-context-utils';
import { Button } from './button';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import {
  CATALOG_ITEM_CARD_MARGIN,
  CATALOG_ITEM_CARD_PADDING,
  CatalogItemCard,
} from './catalog-item-card';
import { ChatStatus } from '@/enums';
import { createStyles, useTheme } from '@/contexts/theme-context';
import {
  EXPANDED_QUANTITY_WIDTH,
  ORDER_ITEM_QUANTITY_HEIGHT,
} from './quantity-selector';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ItemListOption } from '@/contexts/item-context-types';
import { ItemSubtitleV2 } from './item-subtitle-v2';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { MinimalOrderableItemActionButton } from './orderable-item-action-button';
import { OrderableItemListItemImage } from './orders/orderable-item-list-item-image';
import { OrderGuideCarouselType } from '@/contexts/dynamic-config';
import { RenderActionsFn } from './orders/item-detail-actions';
import { SampleRequest } from './ads/sample-request';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import {
  useAddToOrderDisabled,
  useAvailabilityStatusEnabled,
} from '@/hooks/use-add-to-order-disabled';
import {
  useItemDisplayOptions,
  useNewOrderContext,
} from '@/contexts/new-order-context';
import { useItemLabels } from '@/hooks/use-item-labels';
import { useItemVisibleQuantity } from '@/stores/new-order-quantity-store';
import { useUserContext } from '@/contexts/user-context';
import { useVariantPackInfoContext } from '@/contexts/variant-pack-info-context';

const MAXIMUM_IMAGE_WIDTH = 122;
const MAXIMUM_IMAGE_HEIGHT = 100;
const MINIMAL_ORDERABLE_ITEM_WIDTH =
  EXPANDED_QUANTITY_WIDTH +
  2 * (CATALOG_ITEM_CARD_MARGIN + CATALOG_ITEM_CARD_PADDING);

interface Props {
  fromCarousel?: OrderGuideCarouselType;
  item: ItemListOption;
  onSelect: () => void;
  renderActions?: RenderActionsFn;
  size?: 'small' | 'large';
  source: string;
  style?: StyleProp<ViewStyle>;
  updateQuantity: UpdateQuantityFn;
  width?: number;
}

export const MinimalOrderableItemListItem: React.FC<Props> = ({
  fromCarousel,
  item,
  onSelect,
  renderActions,
  size = 'small',
  source,
  style,
  updateQuantity,
  width,
}) => {
  const [styles] = useStyles();
  const theme = useTheme();
  const metadata = item.metadata!;
  const { essentialVariantPackUUIDs } = useVariantPackInfoContext();
  const availabilityStatusEnabled = useAvailabilityStatusEnabled();
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const { supplierTimezone } = useNewOrderContext();
  const {
    selectedRestaurantRelationship: { chatStatus },
  } = useUserContext();

  const isProspect = chatStatus === ChatStatus.PROSPECT;
  const isAddToOrderDisabled = useAddToOrderDisabled(metadata.variantPackUUID);
  const variantPackUUID = metadata.variantPackUUID;
  const quantity = useItemVisibleQuantity(variantPackUUID);
  const isEssential = essentialVariantPackUUIDs.has(variantPackUUID);

  const renderAsSmall = size === 'small';
  const renderAsLarge = size === 'large';

  const isAddToOrderPrebookItemDisabled = useMemo(
    () => addToOrderPrebookItemDisabled(metadata, supplierTimezone),
    [metadata, supplierTimezone],
  );

  const itemLabels = useItemLabels(metadata, false, renderAsSmall);

  const subtitle = (
    <ItemSubtitleV2
      currencyCode={metadata.currencyCode}
      displayString={metadata.packDisplayString}
      labels={itemLabels}
      promotionPriceInMicros={metadata.promotionPricePerUnitInMicros}
      sku={metadata.externalID}
      style={styles.subtitle}
      unitCount={metadata.unitCount}
      unitPriceInMicros={metadata.unitPriceInMicros}
    />
  );

  const actionButton = renderActions ? (
    renderActions(metadata)
  ) : (
    <MinimalOrderableItemActionButton
      availabilityStatusEnabled={availabilityStatusEnabled}
      isAddToOrderDisabled={isAddToOrderDisabled}
      isAddToOrderPrebookItemDisabled={isAddToOrderPrebookItemDisabled}
      isEssential={isEssential}
      isProspect={isProspect}
      metadata={metadata}
      onSelect={onSelect}
      quantity={quantity}
      quantityPillStyle={styles.quantityPill}
      renderAsSmall={renderAsSmall}
      renderMultiSkuButton={({
        onSelect: handleSelect,
        variantPackUUID: multiSkuVariantPackUUID,
      }) => (
        <Button
          onPress={handleSelect}
          rounded
          size="small"
          style={styles.seeOptionsButton}
          textProps={{
            numberOfLines: 1,
          }}
          textStyle={[TYPOGRAPHY.small, styles.seeOptionsText]}
          trackingData={{
            name: 'orderable_catalog_item.see_options',
            properties: {
              variantPackUUID: multiSkuVariantPackUUID,
            },
          }}
        >
          {i18n.t('see_options')}
        </Button>
      )}
      unavailableTextStyle={[TYPOGRAPHY.pSemiBold, styles.unavailableText]}
      unavailableWrapperStyle={styles.unavailableWrapper}
      updateQuantity={updateQuantity}
      variantPackUUID={variantPackUUID}
    />
  );

  const largeImageProps = renderAsSmall
    ? {}
    : {
        imageSize: Math.min(width ?? MAXIMUM_IMAGE_WIDTH, MAXIMUM_IMAGE_WIDTH),
        listDisplaySize: ListDisplaySize.Large,
      };
  const altText = [metadata.displayName, metadata.externalID]
    .filter(Boolean)
    .join('-');

  const firstBadge = metadata.badges?.[0];
  const badgeOverlayStyle = firstBadge?.background_color
    ? { backgroundColor: firstBadge.background_color }
    : null;
  const badgeContainerStyle = firstBadge?.short_name
    ? styles.badgeContainerWithLabel
    : null;
  const badgeComponent = firstBadge?.image_url ? (
    <View style={styles.badgeContent}>
      <CachedImage
        alt={`${firstBadge.name} badge`}
        contentFit="contain"
        source={{ uri: firstBadge.image_url }}
        style={
          firstBadge.short_name ? styles.badgeImageFixed : styles.badgeImage
        }
      />
      {firstBadge.short_name ? (
        <Typography
          color={theme.sk.color.text.primary}
          numberOfLines={1}
          variant="paragraph-sm"
        >
          {firstBadge.short_name}
        </Typography>
      ) : null}
    </View>
  ) : undefined;

  return (
    <CatalogItemCard
      onPress={onSelect}
      style={[
        styles.base,
        style,
        renderAsLarge && typeof width === 'number' ? { width } : null,
      ]}
    >
      {fromCarousel ? (
        <>
          <View style={styles.carouselItemImageWrapper}>
            <OrderableItemListItemImage
              alt={altText}
              badge={badgeComponent}
              badgeStyle={[badgeContainerStyle, badgeOverlayStyle]}
              hidePlaceholderImages={hidePlaceholderImages}
              imageSize={MAXIMUM_IMAGE_HEIGHT}
              imageURLs={metadata.photoURLs}
            />
          </View>
          <View style={styles.carouselItemTitleWrapper}>
            <Text numberOfLines={1} style={[TYPOGRAPHY.pSemiBold]}>
              {item.title}
            </Text>
          </View>
          <View style={styles.carouselItemImageWrapper}>{subtitle}</View>
          {fromCarousel === OrderGuideCarouselType.SAMPLE ? (
            <SampleRequest
              buttonText="sample_request.try_it"
              option={item}
              source={source}
            />
          ) : (
            <View style={styles.quantityWrapperSmall}>{actionButton}</View>
          )}
        </>
      ) : (
        <View style={styles.notFromCarousel}>
          <View
            style={
              renderAsSmall
                ? styles.imageWrapperSmall
                : styles.imageWrapperLarge
            }
          >
            <OrderableItemListItemImage
              hidePlaceholderImages={hidePlaceholderImages}
              {...largeImageProps}
              alt={altText}
              badge={badgeComponent}
              badgeStyle={[badgeContainerStyle, badgeOverlayStyle]}
              imageURLs={metadata.photoURLs}
            />
          </View>
          <View style={styles.titleWrapper}>
            <Text
              numberOfLines={1}
              style={[TYPOGRAPHY.pSemiBold, styles.title]}
            >
              {item.title}
            </Text>
            {renderAsLarge ? (
              <Text numberOfLines={1} style={TYPOGRAPHY.small}>
                {metadata.description}
              </Text>
            ) : (
              subtitle
            )}
          </View>
          {renderAsLarge ? subtitle : null}
          <View
            style={
              renderAsSmall
                ? styles.quantityWrapperSmall
                : styles.quantityWrapperLarge
            }
          >
            {actionButton}
          </View>
        </View>
      )}
    </CatalogItemCard>
  );
};

const useStyles = createStyles((theme) => ({
  badgeContainerWithLabel: {
    height: 'auto',
    paddingHorizontal: 4,
    paddingVertical: 2,
    width: 'auto',
  },
  badgeContent: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: 4,
  },
  badgeImage: {
    flex: 1,
  },
  badgeImageFixed: {
    height: 24,
    width: 24,
  },
  base: {
    width: MINIMAL_ORDERABLE_ITEM_WIDTH,
  },
  carouselItemImageWrapper: {
    alignItems: 'center',
    marginBottom: 8,
  },
  carouselItemTitleWrapper: {
    marginBottom: 4,
  },
  imageWrapperLarge: {
    alignItems: 'center',
    marginBottom: 4,
  },
  imageWrapperSmall: {
    alignItems: 'center',
  },
  notFromCarousel: {
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
  },
  quantityPill: {
    maxWidth: 72,
  },
  quantityWrapperLarge: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
    width: '100%',
  },
  quantityWrapperSmall: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  seeOptionsButton: {
    backgroundColor: theme.colors.accent,
    height: ORDER_ITEM_QUANTITY_HEIGHT,
    paddingHorizontal: 16,
  },
  seeOptionsText: {
    ...FONTS.primaryBold,
    color: theme.colors.accentText,
  },
  subtitle: {
    justifyContent: 'flex-start',
  },
  title: {
    marginBottom: 4,
  },
  titleWrapper: {
    marginBottom: 4,
  },
  unavailableText: {
    color: theme.colors.inactiveText,
  },
  unavailableWrapper: {
    height: ORDER_ITEM_QUANTITY_HEIGHT,
    justifyContent: 'center',
  },
}));
